import { UniversalNodeRegistry } from '@/features/backtest/schemas/universal-node-schema'
import { materializeNode } from '@/features/backtest/graph-engine/materialize'
import { ENTRY_WINDOW_HANDLE } from '@/features/backtest/schemas/node-schemas/entry-window-schema'
import {
  TRADE_MANAGER_TYPE,
  tradeManagerSchema,
} from '@/features/backtest/schemas/node-schemas/trade-manager-schema'
import { parseTimeframeToMinutes } from './price-data-ownership'
import { freshId, readParams, type LooseEdge, type LooseNode } from './graph-lowering'

export { TRADE_MANAGER_TYPE }

/**
 * The Trade Manager, expanded for the engine.
 *
 * The engine has no Trade Manager. It has Advanced Trade (`trade`) and its
 * satellites, so before a graph is sent each Trade Manager becomes exactly the
 * cluster the Guided Form compiles (form-builder/compile-form.ts
 * planAdvancedCluster): a `trade` node that keeps the Trade Manager's id (so
 * its Trigger wire survives untouched, and an engine error on the trade lands
 * on the node the user sees), an Entry Rules, a Target, a Stop and a Position
 * Sizing, plus a Trailing Stop when trailing is on.
 *
 *   - A typed target or stop (PnL %, Ticks) reads the primary price feed's
 *     close, as every hand-built and form-built cluster does; the engine
 *     computes those off the entry price.
 *   - A Price level reads the wired level, a Condition target (and a
 *     Condition trailing stop) the wired true/false.
 *   - A Multi Target target (stored as 'Custom') wires the user's own Multi
 *     Target node straight into the trade, with no generated target.
 *   - The generated Stop also feeds Position Sizing: Risk ($) divides by its
 *     distance, the other methods ignore it.
 *   - A Limit entry reads the wired limit price; Market reads the price feed.
 *   - The entry window moves to the generated Entry Rules, where
 *     lowerEntryWindows (which runs next) turns it into a Time Signal and a
 *     Merge on the trade's trigger. A saved wire into the retired Window
 *     socket moves with it.
 *
 * Wires into sockets the settings do not use are dropped. Generated ids are
 * `<tradeManagerId>-entry`, `-target`, `-stop`, `-sizing`, `-trailing` (or a
 * numbered variant when the canvas holds one); `owners` maps each to its Trade
 * Manager for readiness and run errors (buildEngineView's `ownerOf`).
 *
 * Pure; returns the input arrays when the graph has no Trade Manager.
 */

/** The Trade Manager's setting, else its schema default. */
function settingsOf(node: LooseNode): Record<string, unknown> {
  const stored = readParams(node)
  const out: Record<string, unknown> = {}
  for (const def of tradeManagerSchema.parameters) out[def.name] = stored[def.name] ?? def.default
  return out
}

/** The `isPrimary` Price Data, else the one on the finest timeframe. */
function primaryPrice(nodes: LooseNode[]): LooseNode | null {
  const prices = nodes.filter((n) => n.type === 'priceData')
  const primary = prices.find((n) => (n.data as { isPrimary?: boolean })?.isPrimary === true)
  if (primary) return primary
  let best: LooseNode | null = null
  let bestMinutes = Infinity
  for (const node of prices) {
    const minutes = parseTimeframeToMinutes(String(readParams(node).timeframe ?? '1m'))
    if (minutes < bestMinutes) {
      best = node
      bestMinutes = minutes
    }
  }
  return best
}

const PRICE_OUTPUT = 'ohlcv'

type InputEntry = Record<string, unknown> & { name: string }

/**
 * Lower every Trade Manager: the Trade Manager schema's declared lowering
 * (`compound.lower`). toEngineView runs it on the canonical editor graph
 * (proxy wires dropped) before serialization and before the entry-window
 * facet, so the generated nodes are serialized exactly like hand-built ones.
 */
export function lowerTradeManagers<N extends LooseNode, E extends LooseEdge>(
  nodes: N[],
  edges: E[],
): { nodes: N[]; edges: E[]; owners: Map<string, string> } {
  const owners = new Map<string, string>()
  const managers = nodes.filter((n) => n.type === TRADE_MANAGER_TYPE)
  if (managers.length === 0) return { nodes, edges, owners }

  const taken = new Set(nodes.map((n) => n.id))
  const price = primaryPrice(nodes)
  const managerIds = new Set(managers.map((n) => n.id))

  const replaced = new Map<string, N>()
  const added: N[] = []
  const addedEdges: E[] = []

  for (const manager of managers) {
    const s = settingsOf(manager)
    const inputsOf = (manager.data as { inputs?: InputEntry[] } | undefined)?.inputs ?? []
    const into = (handle: string) =>
      edges.find((e) => e.target === manager.id && e.targetHandle === handle) ?? null
    const at = manager.position ?? { x: 0, y: 0 }

    const make = (
      id: string,
      type: string,
      params: Record<string, unknown>,
      dx: number,
      dy: number,
    ): { id: string; node: N; inputs: InputEntry[] } => {
      const schema = UniversalNodeRegistry.getSchema(type)
      // Without it the trade would quietly vanish from the run. Fail loudly instead.
      if (!schema) throw new Error(`Trade Manager: the "${type}" node schema is not registered`)
      const { node } = materializeNode(
        { id, type, params: params as Record<string, string | number | boolean> },
        { schemaById: new Map([[id, schema]]), position: { x: at.x + dx, y: at.y + dy } },
      )
      const inputs = [...((node.data as { inputs?: InputEntry[] }).inputs ?? [])]
      return {
        id,
        inputs,
        node: {
          ...node,
          ...(manager.parentId ? { parentId: manager.parentId } : {}),
          data: { ...node.data, inputs },
        } as unknown as N,
      }
    }

    // One wire into a generated node, with the input entry serialization reads.
    const connect = (
      target: { id: string; inputs: InputEntry[] },
      handle: string,
      source: string,
      sourceHandle: string | null | undefined,
      entry: Partial<InputEntry>,
    ) => {
      addedEdges.push({
        id: `e-${source}-${sourceHandle ?? ''}-${target.id}-${handle}`,
        source,
        sourceHandle,
        target: target.id,
        targetHandle: handle,
      } as unknown as E)
      const index = target.inputs.findIndex((i) => i.name === handle)
      const next: InputEntry = {
        inputType: 'node',
        value: null,
        ...entry,
        name: handle,
        source,
      }
      if (index >= 0) target.inputs[index] = next
      else target.inputs.push(next)
    }

    const fromPrice = (target: { id: string; inputs: InputEntry[] }, handle: string) => {
      if (price) connect(target, handle, price.id, PRICE_OUTPUT, { field: 'close' })
    }

    // A Trade Manager socket's wire, moved onto a generated node's input. The
    // socket's own entry comes along, so a picked field (High, Low) survives.
    const moveWire = (
      socket: string,
      target: { id: string; inputs: InputEntry[] },
      handle: string,
    ): boolean => {
      const wire = into(socket)
      if (!wire) return false
      const entry = inputsOf.find((i) => i?.name === socket) ?? {}
      connect(target, handle, wire.source, wire.sourceHandle, entry)
      return true
    }

    // The trade itself, on the Trade Manager's id and spot.
    const trade = make(
      manager.id,
      'trade',
      { enableTrailing: s.enableTrailing === true, maxEntries: s.maxEntries },
      0,
      0,
    )
    const triggerEntry = inputsOf.find((i) => i?.name === 'trigger')
    if (triggerEntry) {
      const index = trade.inputs.findIndex((i) => i.name === 'trigger')
      if (index >= 0) trade.inputs[index] = { ...trade.inputs[index], ...triggerEntry }
    }
    replaced.set(manager.id, {
      ...manager,
      type: 'trade',
      data: (trade.node as LooseNode).data,
    } as N)

    // Claims a free id and records the Trade Manager as its owner.
    const satellite = (suffix: string) => {
      const id = freshId(`${manager.id}-${suffix}`, taken)
      owners.set(id, manager.id)
      return id
    }

    // Entry Rules: the order.
    const entry = make(
      satellite('entry'),
      'entryRules',
      {
        entryType: s.entryType,
        orderDirection: s.orderDirection,
        offset: s.offset,
        expirationType: s.expirationType,
        expirationDuration: s.expirationDuration,
        expirationUnit: s.expirationUnit,
        expirationTime: s.expirationTime,
        entryWindow: s.entryWindow,
        entryWindowStart: s.entryWindowStart,
        entryWindowEnd: s.entryWindowEnd,
      },
      -260,
      -195,
    )
    if (s.entryType === 'Limit') moveWire('limitPrice', entry, 'signal')
    else fromPrice(entry, 'signal')
    moveWire(ENTRY_WINDOW_HANDLE, entry, ENTRY_WINDOW_HANDLE)
    added.push(entry.node)
    connect(trade, 'entry', entry.id, 'entry', {})

    // Target.
    if (s.targetType === 'Custom') {
      const wire = into('targetCustom')
      if (wire) connect(trade, 'target', wire.source, wire.sourceHandle, {})
    } else {
      const target = make(
        satellite('target'),
        'target',
        {
          targetType: s.targetType,
          percentageValue: s.targetValue,
          priceOffset: s.targetOffset,
        },
        -260,
        -65,
      )
      if (s.targetType === 'Static Value') moveWire('targetLevel', target, 'signal')
      else if (s.targetType === 'Condition') moveWire('targetWhen', target, 'signal')
      else fromPrice(target, 'signal')
      added.push(target.node)
      connect(trade, 'target', target.id, 'target', {})
    }

    // Stop, which Position Sizing reads too.
    const stop = make(
      satellite('stop'),
      'stop',
      { stopType: s.stopType, percentageValue: s.stopValue, priceOffset: s.stopOffset },
      -260,
      65,
    )
    if (s.stopType === 'Static Value') moveWire('stopLevel', stop, 'signal')
    else fromPrice(stop, 'signal')
    added.push(stop.node)
    connect(trade, 'exit', stop.id, 'stop', {})

    // Position Sizing.
    const sizing = make(
      satellite('sizing'),
      'positionSizing',
      { method: s.sizingMethod, value: s.sizingValue },
      -260,
      195,
    )
    connect(sizing, 'signal', stop.id, 'stop', {})
    added.push(sizing.node)
    connect(trade, 'sizing', sizing.id, 'sizing', {})

    // Trailing Stop, only when on. Its typed modes read no signal; Condition
    // reads the wired true/false (the engine exits at market once it is true).
    if (s.enableTrailing === true) {
      const trailing = make(
        satellite('trailing'),
        'trailingStop',
        {
          trailType: s.trailType,
          trailDistance: s.trailDistance,
          activationTrigger: s.activationTrigger,
          activationValue: s.activationValue,
        },
        -260,
        325,
      )
      if (s.trailType === 'Condition') moveWire('trailWhen', trailing, 'signal')
      added.push(trailing.node)
      connect(trade, 'trailing', trailing.id, 'stop', {})
    }
  }

  return {
    nodes: [...nodes.map((n) => replaced.get(n.id) ?? n), ...added],
    edges: [
      // Every wire into a Trade Manager is re-made above, except its Trigger,
      // which already lands on the trade (same id, same socket).
      ...edges.filter((e) => !managerIds.has(e.target) || e.targetHandle === 'trigger'),
      ...addedEdges,
    ],
    owners,
  }
}
